import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faMobileAlt,
  faGraduationCap,
  faCheckSquare,
  faSave,
  faFileAlt,
  faSlidersH,
  faInfoCircle,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import MiniLoader from "../Shared/MiniLoader";
import { toastHelper } from "../../Helpers/toastHelper";
import extractErrorMessage from "../../Helpers/APIHelper";
import { useLanguage } from '../../contexts/LanguageContext';

const Settings = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [expandedSection, setExpandedSection] = useState('exam');

  const [settings, setSettings] = useState({
    allowExamOnMobile: false,
    passingScore: 0,
    notificationsEnabled: true,
    autoSaveExams: true,
    examTimeBuffer: 5,
    showStudentProgress: true,
    autoGradeSubmissions: true,
    allowStudentFeedback: true,
    allowPrintExams: true,
    showAnswersAfterExam: false,
    maximumAttemptsPerExam: 1,
    defaultExamDuration: 60,
    honorBoardExamsCount: 5,
  });

  const settingSections = [
    {
      id: "exam",
      title: t('examSettingsTitle'),
      icon: faFileAlt,
      description: t('examSettingsDescription'),
      settings: [
        {
          id: "passingScore",
          name: t('defaultPassingScoreLabel'),
          description: t('defaultPassingScoreDesc'),
          type: "number",
          icon: faGraduationCap,
          min: 0,
          max: 100,
        },
        {
          id: "allowExamOnMobile",
          name: t('allowMobileDevicesLabel'),
          description: t('allowMobileDevicesDesc'),
          type: "toggle",
          icon: faMobileAlt,
        },
        {
          id: "honorBoardExamsCount",
          name: t('honorBoardExamsCount'),
          description: t('honorBoardExamsCountDesc'),
          type: "number",
          icon: faCheckSquare,
          min: 1,
          max: 50,
        },
      ],
    },
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await ApiServices.get("Setting/Teacher");
        if (response.check && response.data) {
          mapResponseToSettings(response.data);
        }
      } catch (error) {
        toastHelper.error(t('settingsLoadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const mapResponseToSettings = (data) => {
    const newSettings = {
      ...settings,
      allowExamOnMobile: data.allow_exam_on_mobile,
      honorBoardExamsCount: data.honor_board_exams_count,
      passingScore: data.passing_score
    };

    setSettings(newSettings);
    setOriginalSettings(newSettings);
  };

  const handleToggleSetting = (settingId) => {
    const newValue = !settings[settingId];
    const updatedSettings = { ...settings, [settingId]: newValue };
    setSettings(updatedSettings);
    checkForChanges(updatedSettings);
  };

  const handleNumericChange = (settingId, value) => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, ""));
    if (isNaN(numericValue)) return;

    const settingDefinition = settingSections
      .flatMap((section) => section.settings)
      .find((s) => s.id === settingId);

    if (settingDefinition) {
      const { min, max } = settingDefinition;
      if (min !== undefined && numericValue < min) return;
      if (max !== undefined && numericValue > max) return;
    }

    const updatedSettings = { ...settings, [settingId]: numericValue };
    setSettings(updatedSettings);
    checkForChanges(updatedSettings);
  };

  const checkForChanges = (currentSettings) => {
    const hasAnyChanges = Object.keys(currentSettings).some(
      (key) => currentSettings[key] !== originalSettings[key]
    );
    setHasChanges(hasAnyChanges);
  };

  const handleSaveAllSettings = async () => {
    try {
      setSavingAll(true);
      
      const payload = {
        allow_exam_on_mobile: settings.allowExamOnMobile,
        honor_board_exams_count: settings.honorBoardExamsCount,
        passing_score: settings.passingScore,
      };

      await ApiServices.put("Setting/Teacher/update", payload);

      setOriginalSettings(settings);
      setHasChanges(false);
      toastHelper.success(t('settingsSaveSuccess'));
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        toastHelper.error(extractErrorMessage(error));
      } else {
        toastHelper.error(t('failedToSaveSettings'));
      }
    } finally {
      setSavingAll(false);
    }
  };

  if (loading) return <Loader text={t('loadingSettings')} />;

  return (
    <div className="modern-settings-page">
      {/* Header Section */}
      <div className="settings-hero">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="hero-text"
          >
            <h1><FontAwesomeIcon icon={faSlidersH} /> {t('systemSettings')}</h1>
            <p>{t('systemSettingsSubtitle')}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="hero-action"
          >
            <button
              className={`save-all-btn ${hasChanges ? 'active' : ''}`}
              onClick={handleSaveAllSettings}
              disabled={!hasChanges || savingAll}
            >
              {savingAll ? <MiniLoader size={18} color="#fff" /> : <FontAwesomeIcon icon={faSave} />}
              <span>{savingAll ? t('saving') : t('saveChanges')}</span>
            </button>
          </motion.div>
        </div>
      </div>

      <div className="settings-body">
        {settingSections.map((section, idx) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="settings-section-card"
          >
            <div
              className="section-header"
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
            >
              <div className="header-info">
                <div className="icon-box">
                  <FontAwesomeIcon icon={section.icon} />
                </div>
                <div>
                  <h3>{section.title}</h3>
                  <p>{section.description}</p>
                </div>
              </div>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`expand-icon ${expandedSection === section.id ? 'rotated' : ''}`}
              />
            </div>

            <AnimatePresence>
              {expandedSection === section.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="section-body"
                >
                  <div className="settings-grid">
                    {section.settings.map((setting) => (
                      <div key={setting.id} className="setting-item-card">
                        <div className="setting-info">
                          <div className="setting-icon-small">
                            <FontAwesomeIcon icon={setting.icon} />
                          </div>
                          <div className="text">
                            <h4>{setting.name}</h4>
                            <p>{setting.description}</p>
                          </div>
                        </div>

                        <div className="setting-control">
                          {setting.type === 'toggle' ? (
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={settings[setting.id]}
                                onChange={() => handleToggleSetting(setting.id)}
                              />
                              <span className="slider round"></span>
                            </label>
                          ) : (
                            <div className="numeric-input-wrapper">
                              <input
                                type="text"
                                value={settings[setting.id]}
                                onChange={(e) => handleNumericChange(setting.id, e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="section-footer">
                    <p><FontAwesomeIcon icon={faInfoCircle} /> {t('autoAppliedInfoNote')}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      <style jsx>{`
        .modern-settings-page {
            font-family: "Zain", sans-serif;
            background: transparent;
            min-height: 100%;
        }

        /* Hero Section */
        .settings-hero {
            background: white;
            border-radius: 32px;
            padding: 35px 40px;
            margin-bottom: 30px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
            border: 1px solid rgba(255, 255, 255, 1);
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .hero-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
            gap: 20px;
        }

        .hero-text h1 {
            font-size: 2rem;
            color: #0F172A;
            font-weight: 900;
            margin: 0 0 10px 0;
            display: flex;
            align-items: center;
            gap: 15px;
            letter-spacing: -0.5px;
        }

        .hero-text h1 svg {
            color: #6366F1;
        }

        .hero-text p {
            color: #64748B;
            font-weight: 600;
            font-size: 1.1rem;
            margin: 0;
        }

        /* Save Button */
        .save-all-btn {
            background: #F8FAFC;
            color: #94A3B8;
            border: none;
            padding: 14px 32px;
            border-radius: 50px;
            font-size: 1.05rem;
            font-weight: 800;
            display: flex;
            align-items: center;
            gap: 10px;
            cursor: not-allowed;
            transition: all 0.3s ease;
            font-family: inherit;
        }

        .save-all-btn.active {
            background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
            color: white;
            cursor: pointer;
            box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
        }

        .save-all-btn.active:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 25px rgba(99, 102, 241, 0.35);
        }

        /* Settings Cards */
        .settings-body {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        .settings-section-card {
            background: white;
            border-radius: 24px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
            border: 1px solid rgba(255, 255, 255, 1);
            transition: all 0.3s ease;
        }

        .section-header {
            padding: 24px 30px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            cursor: pointer;
            background: white;
            transition: background 0.2s;
        }

        .section-header:hover {
            background: #F8FAFC;
        }

        .header-info {
            display: flex;
            align-items: center;
            gap: 20px;
        }

        .icon-box {
            width: 55px;
            height: 55px;
            border-radius: 16px;
            background: #EEF2FF;
            color: #6366F1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }

        .header-info h3 {
            margin: 0 0 5px 0;
            font-size: 1.3rem;
            color: #1E293B;
            font-weight: 800;
        }

        .header-info p {
            margin: 0;
            color: #64748B;
            font-size: 0.95rem;
            font-weight: 600;
        }

        .expand-icon {
            color: #94A3B8;
            transition: transform 0.3s;
        }

        .expand-icon.rotated {
            transform: rotate(180deg);
        }

        .section-body {
            border-top: 2px dashed #F1F5F9;
            background: #FCFCFD;
        }

        .settings-grid {
            padding: 30px;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
            gap: 24px;
        }

        .setting-item-card {
            background: white;
            border: 1px solid #E2E8F0;
            border-radius: 16px;
            padding: 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
        }
        
        .setting-item-card:hover {
            border-color: #6366F1;
            box-shadow: 0 10px 20px rgba(99, 102, 241, 0.08);
            transform: translateY(-2px);
        }

        .setting-info {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
        }

        .setting-icon-small {
            color: #6366F1;
            font-size: 1.1rem;
            width: 36px;
            height: 36px;
            background: #EEF2FF;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .text h4 {
            margin: 0 0 4px 0;
            font-size: 1.1rem;
            color: #0F172A;
            font-weight: 800;
        }

        .text p {
            margin: 0;
            font-size: 0.85rem;
            color: #64748B;
            line-height: 1.4;
            font-weight: 600;
        }

        /* Toggle Switch */
        .toggle-switch {
            position: relative;
            display: inline-block;
            width: 54px;
            height: 30px;
        }

        .toggle-switch input { 
            opacity: 0;
            width: 0;
            height: 0;
        }

        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #E2E8F0;
            transition: .4s;
        }

        .slider:before {
            position: absolute;
            content: "";
            height: 24px;
            width: 24px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: .4s;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        input:checked + .slider {
            background-color: #6366F1;
        }

        input:checked + .slider:before {
            transform: translateX(24px);
        }

        .slider.round {
            border-radius: 50px;
        }

        .slider.round:before {
            border-radius: 50%;
        }

        /* Numeric Input */
        .numeric-input-wrapper input {
            width: 80px;
            padding: 10px;
            border: 2px solid #E2E8F0;
            border-radius: 12px;
            text-align: center;
            font-family: inherit;
            font-weight: 800;
            color: #1E293B;
            outline: none;
            transition: all 0.2s;
        }

        .numeric-input-wrapper input:focus {
            border-color: #6366F1;
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
        }

        .section-footer {
            padding: 16px 30px;
            background: #F8FAFC;
            color: #64748B;
            font-size: 0.9rem;
            font-weight: 600;
            border-top: 2px dashed #E2E8F0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        @media (max-width: 768px) {
            .settings-hero {
                flex-direction: column;
                align-items: flex-start;
                padding: 24px;
            }
            .hero-action { width: 100%; margin-top: 15px; }
            .save-all-btn { width: 100%; justify-content: center; }
            
            .settings-grid {
                grid-template-columns: 1fr;
                padding: 20px;
            }

            .setting-item-card {
                flex-direction: column;
                align-items: flex-start;
            }

            .setting-control {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                margin-top: 15px;
            }
        }
      `}</style>
    </div>
  );
};

export default Settings;
